import {
  type PaymentMethodDescription,
  type PaymentType as PayableType,
  type PendingDocumentResponse,
} from "@/api-v3";
import { CardPaymentMethodType } from "@/api-v3/model/cardPaymentMethodType";
import { PhysicalCardType } from "@/api-v3/model/physicalCardType";

export enum PaymentType {
  BANK_ACCOUNT = "BANK_ACCOUNT",
  DEBIT_CARD = "DEBIT_CARD",
  VIRTUAL_CARD = "VIRTUAL_CARD",
  PHYSICAL_CARD = "PHYSICAL_CARD",
  // Highnote wire names for Spend Cards. PPS labels them "Virtual spend card"
  // and "Physical spend card", so participants only ever read Spend Card copy.
  EXPENSE_CARD = "EXPENSE_CARD",
  PHYSICAL_EXPENSE_CARD = "PHYSICAL_EXPENSE_CARD",
  VIRTUAL_STIPEND_CARD = "VIRTUAL_STIPEND_CARD",
  PHYSICAL_STIPEND_CARD = "PHYSICAL_STIPEND_CARD",
  VIRTUAL_SPEND_CARD = "VIRTUAL_SPEND_CARD",
  PHYSICAL_SPEND_CARD = "PHYSICAL_SPEND_CARD",
  CHEQUE = "CHEQUE",
}

export enum Provider {
  WISE = "WISE",
  CHECKBOOK = "CHECKBOOK",
  HIGHNOTE = "HIGHNOTE",
  MASSPAY = "MASSPAY",
  STRIPE = "STRIPE",
}

export enum MethodProviderName {
  BANK_ACCOUNT_WISE = "BANK_ACCOUNT_WISE",
  DEBIT_CARD_CHECKBOOK = "DEBIT_CARD_CHECKBOOK",
  CHEQUE_CHECKBOOK = "CHEQUE_CHECKBOOK",
  BANK_ACCOUNT_MASSPAY = "BANK_ACCOUNT_MASSPAY",
  VIRTUAL_CARD_HIGHNOTE = "VIRTUAL_CARD_HIGHNOTE",
  VIRTUAL_CARD_CHECKBOOK = "VIRTUAL_CARD_CHECKBOOK",
  PHYSICAL_CARD_HIGHNOTE = "PHYSICAL_CARD_HIGHNOTE",
  /** Highnote Virtual Spend Card, sent under its `EXPENSE_CARD` wire name. */
  EXPENSE_CARD_HIGHNOTE = "EXPENSE_CARD_HIGHNOTE",
  BANK_ACCOUNT_STRIPE = "BANK_ACCOUNT_STRIPE",
  VIRTUAL_CARD_STRIPE = "VIRTUAL_CARD_STRIPE",
  PHYSICAL_CARD_STRIPE = "PHYSICAL_CARD_STRIPE",
  VIRTUAL_STIPEND_CARD_STRIPE = "VIRTUAL_STIPEND_CARD_STRIPE",
  PHYSICAL_STIPEND_CARD_STRIPE = "PHYSICAL_STIPEND_CARD_STRIPE",
  VIRTUAL_SPEND_CARD_STRIPE = "VIRTUAL_SPEND_CARD_STRIPE",
  PHYSICAL_SPEND_CARD_STRIPE = "PHYSICAL_SPEND_CARD_STRIPE",
}

interface AddressData {
  title: string;
  maxLength: number;
  minLength: number;
  order: number;
  client_type: string;
  type: string;
  options?: { key: string; value: string }[];
}

export type AddressFieldType = "line_1" | "city" | "state" | "zip";

type AddressProperty = Record<AddressFieldType, AddressData>;

export type FieldType =
  | "full_name"
  | "routing"
  | "account"
  | "type"
  | "address"
  | "card_number"
  | "expiration_date"
  | "phone"
  | "email"
  | "given_name"
  | "middle_name"
  | "family_name"
  | "date_of_birth"
  | "swift_code"
  | "payer_name"
  | "bank_name"
  | "first_name"
  | "last_name"
  | "tax_number"
  | "name"
  | "phone_number";

export type ClientType =
  | "month-year"
  | "year-month"
  | "numeric"
  | "phone"
  | "address"
  | "email"
  | "iban"
  | "date"
  | "select"
  | "bank_name";

export type FieldTypes = AddressFieldType | FieldType;

export type PropertyType = "object" | "string";

export interface Property {
  title: string;
  description: string | null;
  property_name: FieldType;
  pattern: string | null;
  type: PropertyType;
  client_type: ClientType | null;
  maxLength: number | null;
  minLength: number | null;
  enum: { key: string; value: string }[] | null;
  properties: AddressProperty | null;
  required_fields: AddressFieldType[] | null;
}

export interface PaymentMethod {
  title: string;
  type: string;
  payment_type: PaymentType;
  provider: Provider;
  payment_country: string;
  required_fields: FieldType[];
  country: string;
  currency: string;
  method_provider_name: MethodProviderName;
  properties: Property[];
  /**
   * Agreements the participant must be shown before creating this method.
   * Acceptance is recorded server-side on creation; never call the accept endpoint.
   */
  legal_documents: PendingDocumentResponse[];
  /** PPS pass-through, e.g. `source_platform` ("US" | "GB" | "EU") for Stripe. */
  metadata?: { source_platform?: string; [key: string]: unknown };
  /**
   * Localized copy shown under the method on the select page. `null` when
   * PASS has no copy for the payment type.
   */
  description?: PaymentMethodDescription | null;
  /**
   * Payable Item kinds an account created from this method can receive. Stipend
   * Cards list stipend and micropayment only, Spend Cards list nothing, every
   * other method lists all four. Absent means PASS sent nothing, which its own
   * fallback treats as all four kinds.
   */
  allowed_payable_types?: PayableType[];
}

export type AvailablePaymentMethodsResponse = PaymentMethod[];

const cardTypeByPaymentType: Partial<
  Record<PaymentType, CardPaymentMethodType>
> = {
  [PaymentType.VIRTUAL_CARD]: CardPaymentMethodType.vc,
  [PaymentType.PHYSICAL_CARD]: CardPaymentMethodType.pc,
  [PaymentType.EXPENSE_CARD]: CardPaymentMethodType.ec,
  [PaymentType.PHYSICAL_EXPENSE_CARD]: CardPaymentMethodType.pec,
  [PaymentType.VIRTUAL_STIPEND_CARD]: CardPaymentMethodType.vstc,
  [PaymentType.PHYSICAL_STIPEND_CARD]: CardPaymentMethodType.pstc,
  [PaymentType.VIRTUAL_SPEND_CARD]: CardPaymentMethodType.vspc,
  [PaymentType.PHYSICAL_SPEND_CARD]: CardPaymentMethodType.pspc,
};

/** The PPS `card_type` a card payment method issues, or null for non-card methods. */
export function getCardTypeForPaymentType(
  paymentType: PaymentType
): CardPaymentMethodType | null {
  return cardTypeByPaymentType[paymentType] ?? null;
}

/**
 * The Card Type behind a PPS payment type name that arrives as a plain string,
 * such as a Payment Account's `target_account_type`, or `undefined` when it
 * names no card. Narrows in one place so no caller casts to `PaymentType`.
 */
export function getCardTypeForAccountType(
  accountType: string
): CardPaymentMethodType | undefined {
  return cardTypeByPaymentType[accountType as PaymentType];
}

export function isVirtualCard(type: PaymentType | MethodProviderName) {
  return (
    type === PaymentType.VIRTUAL_CARD ||
    type === MethodProviderName.VIRTUAL_CARD_HIGHNOTE ||
    type === MethodProviderName.VIRTUAL_CARD_CHECKBOOK ||
    type === MethodProviderName.VIRTUAL_CARD_STRIPE
  );
}

export function isBankAccount(type: PaymentType | MethodProviderName) {
  return (
    type === PaymentType.BANK_ACCOUNT ||
    type === MethodProviderName.BANK_ACCOUNT_WISE ||
    type === MethodProviderName.BANK_ACCOUNT_MASSPAY ||
    type === MethodProviderName.BANK_ACCOUNT_STRIPE
  );
}

export function isCheque(type: PaymentType | MethodProviderName) {
  return (
    type === PaymentType.CHEQUE || type === MethodProviderName.CHEQUE_CHECKBOOK
  );
}

export function isPhysicalCard(type: PaymentType | MethodProviderName) {
  return (
    type === PaymentType.PHYSICAL_CARD ||
    type === MethodProviderName.PHYSICAL_CARD_HIGHNOTE ||
    type === MethodProviderName.PHYSICAL_CARD_STRIPE
  );
}

export function isDebitCard(type: PaymentType | MethodProviderName) {
  return (
    type === PaymentType.DEBIT_CARD ||
    type === MethodProviderName.DEBIT_CARD_CHECKBOOK
  );
}

/** Whether the card a payment method issues is virtual or ships as plastic. */
export type CardFormFactor = "virtual" | "physical";

/**
 * Navigation state for the new-card success page, naming the Card Type just
 * created so the page shows that card rather than another virtual card held.
 */
export interface NewCardSuccessState {
  cardType: CardPaymentMethodType;
}

const isPhysicalCardType = (cardType: CardPaymentMethodType): boolean =>
  (Object.values(PhysicalCardType) as string[]).includes(cardType);

/**
 * Form factor of the card a payment method issues, or `null` when the method
 * issues no card at all. Derived from the Card Type rather than the method
 * name, so it answers for all eight Card Types and a ninth only has to join
 * `cardTypeByPaymentType`. `PhysicalCardType` is the generated enum of the
 * Card Types PPS ships as plastic.
 */
export function getCardFormFactor(
  paymentType: PaymentType
): CardFormFactor | null {
  const cardType = getCardTypeForPaymentType(paymentType);
  if (!cardType) {
    return null;
  }
  return isPhysicalCardType(cardType) ? "physical" : "virtual";
}

/**
 * The programme a card belongs to. Virtual Card and Physical Card are the
 * Consumer Card programme, Stipend Cards receive stipends and micropayments,
 * and Spend Cards pay study costs from the sponsor's pool.
 */
export type CardProgramme = "consumer" | "stipend" | "spend";

// Spend Cards come from two providers: Highnote issues ec/pec (the wire values
// predate the Spend Card name) and Stripe issues vspc/pspc. Both are one
// programme, so participants only ever read Spend Card copy for them.
const cardProgrammeByCardType: Record<CardPaymentMethodType, CardProgramme> = {
  [CardPaymentMethodType.vc]: "consumer",
  [CardPaymentMethodType.pc]: "consumer",
  [CardPaymentMethodType.vstc]: "stipend",
  [CardPaymentMethodType.pstc]: "stipend",
  [CardPaymentMethodType.ec]: "spend",
  [CardPaymentMethodType.pec]: "spend",
  [CardPaymentMethodType.vspc]: "spend",
  [CardPaymentMethodType.pspc]: "spend",
};

/**
 * Programme of the card a payment method issues, or `null` when the method
 * issues no card at all. Derived from the Card Type like `getCardFormFactor`,
 * so a ninth Card Type only has to join the two lookup tables.
 */
export function getCardProgramme(
  paymentType: PaymentType
): CardProgramme | null {
  const cardType = getCardTypeForPaymentType(paymentType);
  return cardType ? cardProgrammeByCardType[cardType] : null;
}

/**
 * The physical Card Type a participant can order from a virtual card they hold.
 * Only the two payout programmes appear: Spend Cards are issued by the study,
 * so their plastic siblings are never ordered from here.
 */
const orderablePhysicalCardTypeByVirtualCardType: Partial<
  Record<CardPaymentMethodType, PhysicalCardType>
> = {
  [CardPaymentMethodType.vc]: PhysicalCardType.pc,
  [CardPaymentMethodType.vstc]: PhysicalCardType.pstc,
};

/**
 * The physical Card Type this virtual card can be upgraded to, or `undefined`
 * when the participant never orders plastic for it.
 */
export function getOrderablePhysicalCardType(
  virtualCardType: CardPaymentMethodType
): PhysicalCardType | undefined {
  return orderablePhysicalCardTypeByVirtualCardType[virtualCardType];
}

/**
 * The payment method a held card was issued from, or `undefined` when it is no
 * longer on offer. Matched on Card Type and provider, the same way PASS ties a
 * card to its programme, so a card reads its agreements and `source_platform`
 * from the method that created it.
 */
export function findPaymentMethodForCard<
  TMethod extends Pick<PaymentMethod, "payment_type" | "provider">,
>(
  methods: TMethod[],
  cardType: CardPaymentMethodType,
  provider: string
): TMethod | undefined {
  return methods.find(
    (method) =>
      getCardTypeForPaymentType(method.payment_type) === cardType &&
      method.provider.toLowerCase() === provider.toLowerCase()
  );
}

/**
 * The payment method that orders the plastic sibling of a virtual card the
 * participant already holds, or `undefined` when none is on offer. Matched on
 * the sibling Card Type and the card's own provider, so ordering from a Stripe
 * Virtual Stipend Card creates a Physical Stipend Card rather than a Highnote
 * Physical Card.
 */
export function findPhysicalCardMethodForVirtualCard<
  TMethod extends Pick<PaymentMethod, "payment_type" | "provider">,
>(
  methods: TMethod[],
  virtualCardType: CardPaymentMethodType,
  provider: string
): TMethod | undefined {
  const physicalCardType = getOrderablePhysicalCardType(virtualCardType);
  if (!physicalCardType) {
    return undefined;
  }
  return methods.find(
    (method) =>
      getCardTypeForPaymentType(method.payment_type) === physicalCardType &&
      method.provider.toLowerCase() === provider.toLowerCase()
  );
}
